/**
 * AccentSwatchPicker — 7 colour swatches for the brand accent.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ .swatches`. Each swatch is
 * a radio-style toggle; the active one gets a sky ring + check chip in
 * the centre. The swatch colour is rendered with a hex value (not a
 * Tailwind class) because the palette includes hues that aren't in our
 * token system (rose, indigo, violet) — and the accent itself is brand
 * data, not a theming concern.
 *
 * V1.0.x note: the value is stored on the company row but doesn't yet
 * drive PDF / email rendering. The chip below the swatches says so
 * honestly so users don't expect immediate visual changes.
 */
import { Check } from "lucide-react";
import { cn } from "@flint/ui";
import { companies as companiesContract } from "@flint/api-contract";

// Single source of truth for the value type — derived from the wire enum.
export type AccentColour = (typeof companiesContract.accentColour.options)[number];

interface SwatchOption {
  value: AccentColour;
  label: string;
  hex: string;
}

const OPTIONS: ReadonlyArray<SwatchOption> = [
  { value: "sky", label: "Sky", hex: "#0284C7" }, // brand default (V1.1 Studio Slate)
  { value: "indigo", label: "Indigo", hex: "#4F46E5" },
  { value: "emerald", label: "Emerald", hex: "#059669" },
  { value: "amber", label: "Amber", hex: "#CA8A04" },
  { value: "rose", label: "Rose", hex: "#DB2777" },
  { value: "violet", label: "Violet", hex: "#7C3AED" },
  { value: "slate", label: "Slate", hex: "#334155" },
];

export interface AccentSwatchPickerProps {
  value: AccentColour;
  onChange: (next: AccentColour) => void;
  disabled?: boolean;
}

export function AccentSwatchPicker({ value, onChange, disabled }: AccentSwatchPickerProps) {
  return (
    <div role="radiogroup" aria-label="Accent colour" className="flex flex-wrap gap-2">
      {OPTIONS.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={opt.label}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            // 44x44 swatch; active gets a 2-px sky ring + offset for visibility
            // against any swatch colour. Hover lifts slightly; focus ring is
            // the same sky ring keyboard users see for everything else.
            className={cn(
              "relative inline-grid place-items-center h-11 w-11 rounded-sm transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
              isActive
                ? "ring-2 ring-sky-600 ring-offset-2 ring-offset-paper scale-105"
                : "hover:scale-105",
              disabled && "opacity-50 cursor-not-allowed",
            )}
            style={{ backgroundColor: opt.hex }}
          >
            {isActive ? (
              <Check size={14} strokeWidth={3} className="text-white drop-shadow" aria-hidden />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
